import { Capacitor } from "@capacitor/core";
import { Intercom } from "@capacitor-community/intercom";
import IntercomWeb, { show } from "@intercom/messenger-js-sdk";
import { useRef } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { useAdonisChat } from "@/modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { get } from "api";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Help from "atoms/icon/icons/help.svg";
import Account from "atoms/icon/icons/ic_account_small_ol.svg";
import Admin from "atoms/icon/icons/ic_admin_small_ol.svg";
import ChevronRight from "atoms/icon/icons/ic_chevron_right_small_ol.svg";
import Logout from "atoms/icon/icons/ic_logout_small_ol.svg";
import MapPin from "atoms/icon/icons/ic_map_pin_ol.svg";
import OrderHistory from "atoms/icon/icons/ic_orderhistory_small_ol.svg";
import QRCode from "atoms/icon/icons/ic_qr_code_small_ol.svg";
import Rewards from "atoms/icon/icons/ic_rewards_ol.svg";
import Notification from "atoms/icon/icons/notification.svg";
import Phone from "atoms/icon/icons/phone.svg";
import { pushNotificationService } from "modules/pushNotifications";
import { SettingsContainer } from "molecules/containers/settings-container/SettingsContainer";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { getFeatureFlag } from "services/Flags";
import Services from "services/Services";
import { waitObj } from "toolbox/Promise";
import { handleLinkClick } from "utils/androidByPassDeepLinkClick";
import { getAdminUrl } from "utils/functions";
import { isNativePlatform } from "utils/platform.helpers";
import { useLogout } from "utils/useLogout";

const load = async () => {
  const platform = await queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );
  const user = await queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const platformLocation = queryClient.fetchQuery(["/platform-location"], () =>
    get("/platform-location")
  );

  const intercomAppId = await getFeatureFlag("intercomAppId");

  if (Capacitor.isNativePlatform()) {
    const isIos = Capacitor.getPlatform() === "ios";
    const userHash = isIos
      ? user.supportChatiOSHash
      : user.supportChatAndroidHash;

    try {
      await Intercom.setUserHash({ hmac: userHash ?? "" });
      await Intercom.registerIdentifiedUser({
        userId: user.id,
        email: user.email ?? undefined,
      });

      await Intercom.updateUser({
        name: `${user.firstName} ${user.lastName}`,
        customAttributes: {
          Patient: true,
        },
      });
    } catch (e) {
      console.error("Intercom error", e);
    }
    await Intercom.hideLauncher();
  } else {
    await IntercomWeb({
      app_id: intercomAppId,
      user_id: user.id,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email ?? undefined,
      user_hash: user.supportChatHash ?? undefined,
      Patient: true,
      hide_default_launcher: true,
    });
  }

  return waitObj({
    platform,
    platformLocation,
    intercomAppId,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const accountLoader: LoaderFunction = async () => {
  if (!(await Services.auth.isAuthorized())) return redirect("/signin");
  else return load();
};

export default function AccountPage() {
  const { platform, platformLocation } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const logout = useLogout({ subdomain: platform?.subdomain });

  const rewardsEnabled = platform?.platformSettings?.rewardsEnabled;

  const { isPersonalisationEnabled } = useAdonisChat();

  const renderHeader = () => (
    <TopNav
      startIconSVG={ChevronLeft}
      onStartIconClick={() => {
        navigate(-1);
      }}
      showHeaderItems={false}
    >
      <div className="flex max-h-[32px] items-center">
        <div className="flex w-full items-center justify-center text-sub2 text-primary">
          YOUR ACCOUNT
        </div>
      </div>
    </TopNav>
  );

  const renderAccountDetailsLink = () => (
    <Group>
      <SettingsContainer to="/account/details" startIconSVG={Account}>
        Account details
      </SettingsContainer>
    </Group>
  );

  const renderOrderHistoryLink = () => (
    <SettingsContainer to="/account/orders" startIconSVG={OrderHistory}>
      Order History
    </SettingsContainer>
  );

  const renderRewardHistoryLink = () =>
    rewardsEnabled && (
      <SettingsContainer to="/account/reward-history" startIconSVG={Rewards}>
        Reward history
      </SettingsContainer>
    );

  const renderVisitHistoryLink = () => (
    <SettingsContainer to="/account/visits" startIconSVG={QRCode}>
      Visit history
    </SettingsContainer>
  );

  const handleContactUsClick = () => {
    isNativePlatform() ? Intercom.displayMessenger() : show();
  };

  const renderContactUsLink = () => (
    <SettingsContainer startIconSVG={Help} onClick={handleContactUsClick}>
      App support
    </SettingsContainer>
  );

  const handlePhoneCallClick = () => {
    if (platformLocation?.phoneNumber) {
      window.open(`tel:${platformLocation.phoneNumber}`);
    }
  };

  const renderPhoneContactLink = () => (
    <SettingsContainer startIconSVG={Phone} onClick={handlePhoneCallClick}>
      Contact us
    </SettingsContainer>
  );

  const renderPersonalisationLink = () =>
    isPersonalisationEnabled && (
      <SettingsContainer to="/personalise" startIconSVG={Account}>
        Personalization
      </SettingsContainer>
    );

  const renderAdminLink = () =>
    !isNativePlatform() && (
      <Group>
        <a
          ref={anchorRef}
          className="text-bold3 text-secondary"
          href={getAdminUrl()}
          target="_self"
          rel="noopener noreferrer"
          onClick={() => {
            handleLinkClick(anchorRef);
          }}
        >
          <div>
            <button className="flex w-full items-center gap-4 bg-white p-4 text-left text-body1 text-primary">
              <Admin className="h-6 w-6 fill-[theme(textColor.secondary)]" />
              <div className="flex-1">Admin</div>
              <div>
                <Icon svg={ChevronRight} color="brandColor" size="xsmall" />
              </div>
            </button>
          </div>
        </a>
      </Group>
    );

  const renderSelectLocationLink = () =>
    isNativePlatform() && (
      <SettingsContainer to="/change-location" startIconSVG={MapPin}>
        Select your location
      </SettingsContainer>
    );

  const renderNotificationSettingsLink = () =>
    isNativePlatform() && (
      <Group>
        <SettingsContainer
          onClick={async () => {
            await pushNotificationService.openAppSettings();
          }}
          startIconSVG={Notification}
        >
          Notification Settings
        </SettingsContainer>
      </Group>
    );

  const renderLogoutLink = () => (
    <Group>
      <div onClick={logout}>
        <SettingsContainer startIconSVG={Logout}>Log out</SettingsContainer>
      </div>
    </Group>
  );

  return (
    <div className="bg-one">
      {renderHeader()}
      <div className="flex min-h-screen flex-col gap-2 bg-one px-4 py-6">
        {renderAccountDetailsLink()}
        <Group>
          {renderOrderHistoryLink()}
          {renderRewardHistoryLink()}
          {renderVisitHistoryLink()}
          {renderContactUsLink()}
          {renderPhoneContactLink()}
          {renderNotificationSettingsLink()}
          {renderSelectLocationLink()}
          {renderPersonalisationLink()}
        </Group>
        {renderAdminLink()}
        {renderLogoutLink()}
      </div>
    </div>
  );
}

const Group = ({ children }: { children: React.ReactNode }) => (
  <div className="divide-y divide-solid divide-background-two overflow-clip rounded">
    {children}
  </div>
);
